import {
  CircleCheck,
  CircleX,
  Clock,
  DoorOpen,
  Sparkles,
  Briefcase,
  History,
  CircleHelp,
  CircleMinus,
  CalendarClock,
  Flame,
  CloudSun,
  Snowflake,
  type LucideIcon,
} from "lucide-react";

// Valores derivados de disponibilidade_efetiva (view v_availability_summary, sql/135+138).
// NÃO expor jargão interno (eixo/sinal/headroom) nos textos: só rótulos limpos pt-BR.
export type DisponibilidadeEfetiva =
  | "alocado_conosco"
  | "vale_abordar_headroom"
  | "vale_abordar_empregado"
  | "vale_abordar_freela"
  | "disponivel"
  | "possivelmente_disponivel"
  | "disponivel_em_breve"
  | "abre_espaco_em_breve"
  | "indisponivel"
  | "provavelmente_indisponivel" // sem produtor vivo desde a M167; só timeline/snapshots antigos
  | "vinculo_sem_projeto"
  | "desconhecido";

export type Frescor = "fresco" | "recente" | "envelhecendo" | "reconfirmar" | "vigente";

// Derivada na view (M170) do status + idade da informação + idade do fato + liberação.
// 'sem_info' fica FORA da régua: ausência de dado não é frieza.
export type Temperatura = "quente" | "morno" | "frio" | "sem_info";

// Composição por eixo, vinda do jsonb `axes`.
export interface AxisInfo {
  value: string;
  source: string;
  frescor?: Frescor;
  confidence?: string;
  observed_at?: string | null;
  observation?: string | null;
}

export interface DisponibilidadeSummary {
  freelancer_id: string;
  emprego: string | null;
  interesse: string | null;
  relacao: string | null;
  frescor_interesse: Frescor | null;
  fonte_relacao: string | null;
  ultimo_sinal: string | null;
  axes: Record<string, AxisInfo> | null;
  disponibilidade_efetiva: DisponibilidadeEfetiva;
  conflito_emprego_interesse: boolean;
  carga_atual: number | null;
  capacity: number | null;
  headroom: number | null;
  capacity_is_floor: boolean | null;
  next_freeing_date: string | null;
  fully_free_date: string | null;
  capacidade_conosco: "alta" | "media" | "baixa" | null;
  tem_projeto_fixo: boolean | null;
  ultima_consulta: string | null;
  reconsulta_due: string | null;
  reconsulta_vencida: boolean | null;
  fim_ultimo_projeto: string | null;
  temperatura: Temperatura | null;
}

interface DisponibilidadeStatusConfig {
  icon: LucideIcon;
  color: string; // cor do ícone/texto
  bg: string; // fundo do chip
  border: string;
  text: string; // rótulo limpo pro usuário
}

const DESCONHECIDO: DisponibilidadeStatusConfig = {
  icon: CircleHelp,
  color: "text-slate-500",
  bg: "bg-slate-50",
  border: "border-slate-200",
  text: "Sem info, vale perguntar",
};

const CONFIG: Record<DisponibilidadeEfetiva, DisponibilidadeStatusConfig> = {
  disponivel: {
    icon: CircleCheck,
    color: "text-emerald-600",
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    text: "Disponível",
  },
  vale_abordar_headroom: {
    icon: Sparkles,
    color: "text-amber-600",
    bg: "bg-amber-50",
    border: "border-amber-200",
    text: "Vale abordar (tem espaço)",
  },
  vale_abordar_empregado: {
    icon: Sparkles,
    color: "text-amber-600",
    bg: "bg-amber-50",
    border: "border-amber-200",
    text: "Vale abordar (empregado, mas topa)",
  },
  vale_abordar_freela: {
    icon: Sparkles,
    color: "text-amber-600",
    bg: "bg-amber-50",
    border: "border-amber-200",
    text: "Vale abordar (freelancer ativo)",
  },
  possivelmente_disponivel: {
    icon: CalendarClock,
    color: "text-teal-600",
    bg: "bg-teal-50",
    border: "border-teal-200",
    text: "Possivelmente disponível",
  },
  disponivel_em_breve: {
    icon: Clock,
    color: "text-teal-600",
    bg: "bg-teal-50",
    border: "border-teal-200",
    text: "Fica livre em breve",
  },
  abre_espaco_em_breve: {
    icon: DoorOpen,
    color: "text-teal-600",
    bg: "bg-teal-50",
    border: "border-teal-200",
    text: "Abre uma vaga em breve",
  },
  alocado_conosco: {
    icon: Briefcase,
    color: "text-blue-600",
    bg: "bg-blue-50",
    border: "border-blue-200",
    text: "Alocado (sem espaço agora)",
  },
  vinculo_sem_projeto: {
    icon: History,
    color: "text-slate-600",
    bg: "bg-slate-50",
    border: "border-slate-200",
    text: "Já trabalhou (vale reconfirmar)",
  },
  provavelmente_indisponivel: {
    icon: CircleMinus,
    color: "text-orange-500",
    bg: "bg-orange-50",
    border: "border-orange-200",
    text: "Provavelmente indisponível",
  },
  indisponivel: {
    icon: CircleX,
    color: "text-rose-500",
    bg: "bg-rose-50",
    border: "border-rose-200",
    text: "Não topa agora",
  },
  desconhecido: DESCONHECIDO,
};

export function getDisponibilidadeConfig(status?: DisponibilidadeEfetiva | null): DisponibilidadeStatusConfig {
  if (!status) return DESCONHECIDO;
  return CONFIG[status] ?? DESCONHECIDO;
}

// Agrupamentos exibidos na base (alguns somam mais de um valor derivado). Compartilhado
// entre os chips de contagem e o filtro clicável da listagem, pra rótulo/valores baterem.
export interface DisponibilidadeBucket {
  label: string;
  representativeValue: DisponibilidadeEfetiva;
  values: DisponibilidadeEfetiva[];
  /** Explicação em linguagem simples pro tooltip do chip. */
  descricao: string;
}
// "Provavelmente indisponível" saiu (sem produtor vivo desde a M167; segue no CONFIG p/ histórico).
export const DISPONIBILIDADE_BUCKETS: DisponibilidadeBucket[] = [
  {
    label: "Vale abordar",
    representativeValue: "vale_abordar_headroom",
    values: ["vale_abordar_headroom", "vale_abordar_empregado", "vale_abordar_freela"],
    descricao:
      "Sinais de que vale puxar conversa: está alocado mas tem espaço, é empregado que topa freela, ou atua como freelancer e nunca dissemos se topa.",
  },
  {
    label: "Disponível",
    representativeValue: "disponivel",
    values: ["disponivel"],
    descricao: "Disse que topa freela. Confira a data: quanto mais antiga a resposta, mais vale reconfirmar.",
  },
  {
    label: "Possivelmente disponível",
    representativeValue: "possivelmente_disponivel",
    values: ["possivelmente_disponivel"],
    descricao:
      "Não topava na época, mas a data que ele deu para reperguntar já passou (ou a resposta tem mais de 3 meses). Boa hora de perguntar de novo.",
  },
  {
    label: "Liberando em breve",
    representativeValue: "disponivel_em_breve",
    values: ["disponivel_em_breve", "abre_espaco_em_breve"],
    descricao: "Tem projeto com a gente terminando nos próximos dias: fica livre ou abre uma vaga na agenda.",
  },
  {
    label: "Alocado",
    representativeValue: "alocado_conosco",
    values: ["alocado_conosco"],
    descricao: "Em projeto com a gente agora, sem espaço para mais.",
  },
  {
    label: "Não topa",
    representativeValue: "indisponivel",
    values: ["indisponivel"],
    descricao: "Disse recentemente que não topa freela no momento.",
  },
  {
    label: "Já trabalhou",
    representativeValue: "vinculo_sem_projeto",
    values: ["vinculo_sem_projeto"],
    descricao: "Já teve projeto com a gente e não está alocado agora. Vale reconfirmar o interesse.",
  },
  {
    label: "Sem info",
    representativeValue: "desconhecido",
    values: ["desconhecido"],
    descricao: "Nenhuma informação de disponibilidade registrada. Vale perguntar.",
  },
];

/**
 * Rótulo pro chip de filtro ativo a partir dos valores selecionados. Com multi-select,
 * a seleção pode ser a UNIÃO de vários buckets (ou um subconjunto): monta o rótulo por
 * bucket coberto e cai pro rótulo do valor individual quando não fecha bucket inteiro.
 */
export function disponibilidadeBucketLabel(values: string[]): string {
  const restante = new Set(values);
  const partes: string[] = [];
  for (const bucket of DISPONIBILIDADE_BUCKETS) {
    if (bucket.values.every((value) => restante.has(value))) {
      partes.push(bucket.label);
      bucket.values.forEach((value) => restante.delete(value));
    }
  }
  for (const value of restante) {
    partes.push(getDisponibilidadeConfig(value as DisponibilidadeEfetiva).text);
  }
  return partes.join(" + ");
}

// Rótulos por eixo (p/ tooltip de composição). Sem jargão.
const EMPREGO_LABEL: Record<string, string> = {
  empregado_fixo: "Empregada",
  freela_ativo: "Freelancer ativo",
  entre_trabalhos: "Entre trabalhos",
  desconhecido: "Sem info",
};
const INTERESSE_LABEL: Record<string, string> = {
  topa_freela: "Topa freela",
  aberto_mas_ocupado: "Aberta, mas ocupada",
  nao_topa: "Não topa agora",
  desconhecido: "Sem info",
};
const RELACAO_LABEL: Record<string, string> = {
  trabalhando_conosco: "Trabalhando com a gente",
  disponivel_em_breve: "Liberando em breve",
  ja_trabalhou_conosco: "Já trabalhou com a gente",
  nunca_trabalhou: "Nunca trabalhou com a gente",
  desconhecido: "Sem info",
};
export const EIXO_LABEL: Record<string, string> = {
  situacao_emprego: "Situação de emprego",
  interesse_freela: "Interesse em freela",
  relacao_conosco: "Relação com a gente",
};
export function axisValueLabel(dimension: string, value?: string | null): string {
  if (!value) return "Sem info";
  if (dimension === "situacao_emprego") return EMPREGO_LABEL[value] ?? value;
  if (dimension === "interesse_freela") return INTERESSE_LABEL[value] ?? value;
  if (dimension === "relacao_conosco") return RELACAO_LABEL[value] ?? value;
  return value;
}

// Classe de badge (fundo suave + texto) por valor de eixo, pra dar cor à composição
// no card de disponibilidade. Espelha as cores das barras da timeline (VALUE_BACKGROUND).
const AXIS_VALUE_BADGE: Record<string, string> = {
  // situacao_emprego
  empregado_fixo: "bg-blue-50 text-blue-700 border-blue-200",
  freela_ativo: "bg-emerald-50 text-emerald-700 border-emerald-200",
  entre_trabalhos: "bg-slate-100 text-slate-600 border-slate-200",
  // interesse_freela
  topa_freela: "bg-emerald-50 text-emerald-700 border-emerald-200",
  aberto_mas_ocupado: "bg-amber-50 text-amber-700 border-amber-200",
  nao_topa: "bg-rose-50 text-rose-700 border-rose-200",
  // relacao_conosco
  trabalhando_conosco: "bg-blue-50 text-blue-700 border-blue-200",
  ja_trabalhou_conosco: "bg-slate-100 text-slate-600 border-slate-200",
  disponivel_em_breve: "bg-teal-50 text-teal-700 border-teal-200",
  nunca_trabalhou: "bg-slate-50 text-slate-500 border-slate-200",
};
const AXIS_VALUE_BADGE_UNKNOWN = "bg-slate-50 text-slate-400 border-dashed border-slate-300";
export function axisValueBadgeClass(value?: string | null): string {
  if (!value || value === "desconhecido") return AXIS_VALUE_BADGE_UNKNOWN;
  return AXIS_VALUE_BADGE[value] ?? "bg-slate-100 text-slate-600 border-slate-200";
}

const FONTE_LABEL: Record<string, string> = {
  projeto_ativo: "projeto ativo",
  projeto_concluido: "projeto concluído",
  manual_humano: "curadoria",
  whatsapp_humano: "WhatsApp",
  whatsapp_ia: "WhatsApp (IA)",
  linkedin: "LinkedIn",
  seed_migracao: "importação",
};
export function fonteLabel(source?: string | null): string {
  if (!source) return "";
  return FONTE_LABEL[source] ?? source;
}

// Frescor -> texto + opacidade (faixas, não matemática fina).
export function frescorLabel(frescor?: Frescor | null): string {
  switch (frescor) {
    case "fresco":
      return "atualizado há poucos dias";
    case "recente":
      return "atualizado no último mês";
    case "envelhecendo":
      return "já tem alguns meses";
    case "reconfirmar":
      return "vale reconfirmar";
    case "vigente":
      return "";
    default:
      return "";
  }
}
export function frescorOpacity(frescor?: Frescor | null): string {
  switch (frescor) {
    case "recente":
      return "opacity-90";
    case "envelhecendo":
      return "opacity-70";
    case "reconfirmar":
      return "opacity-60";
    default:
      return "";
  }
}

// ---------------------------------------------------------------------------
// Temperatura (COR-355): status x tempo, derivada na view (M170). "Sem info"
// fica fora da régua: não dá pra estimar temperatura sem dado.
// ---------------------------------------------------------------------------
export interface TemperaturaConfig {
  icon: LucideIcon;
  color: string;
  bg: string;
  border: string;
  text: string;
  descricao: string;
}
export const TEMPERATURA_CONFIG: Record<Temperatura, TemperaturaConfig> = {
  quente: {
    icon: Flame,
    color: "text-orange-600",
    bg: "bg-orange-50",
    border: "border-orange-200",
    text: "Quente",
    descricao:
      "Alta chance de dizer sim agora: topa freela com resposta do último mês, terminou projeto com a gente há menos de um mês, ou fica livre nos próximos dias.",
  },
  morno: {
    icon: CloudSun,
    color: "text-amber-600",
    bg: "bg-amber-50",
    border: "border-amber-200",
    text: "Morno",
    descricao:
      "Vale investir: resposta positiva de alguns meses, projeto encerrado há menos de seis meses, abre espaço em até um mês, ou é um caso de abordar (empregado que topa, freelancer sem resposta, data de reperguntar vencida).",
  },
  frio: {
    icon: Snowflake,
    color: "text-sky-600",
    bg: "bg-sky-50",
    border: "border-sky-200",
    text: "Frio",
    descricao:
      "Baixa chance agora: não topa com resposta recente, informação positiva com mais de três meses sem reconfirmar, último projeto há mais de seis meses, ou alocado sem liberação próxima.",
  },
  sem_info: {
    icon: CircleHelp,
    color: "text-slate-500",
    bg: "bg-slate-50",
    border: "border-slate-200",
    text: "Sem info",
    descricao: "Nenhum dado para estimar a temperatura. Existem e podem ser filtrados: vale começar perguntando.",
  },
};
export const TEMPERATURAS: Temperatura[] = ["quente", "morno", "frio", "sem_info"];

/**
 * Motivo da temperatura em 1 frase, derivado dos MESMOS campos que a view usa
 * (mesma ordem de precedência do CASE da M170), pro usuário confiar no valor.
 */
export function temperaturaMotivo(summary?: DisponibilidadeSummary | null): string {
  if (!summary || !summary.temperatura || summary.temperatura === "sem_info") {
    return "Sem informação registrada para estimar.";
  }
  const efetiva = summary.disponibilidade_efetiva;
  const frescor = summary.frescor_interesse;
  const carga = summary.carga_atual ?? 0;
  const diasDesde = (dateString: string | null): number | null => {
    if (!dateString) return null;
    const diffMs = Date.now() - new Date(`${dateString.slice(0, 10)}T12:00:00`).getTime();
    return Math.floor(diffMs / 86_400_000);
  };
  const diasFimProjeto = diasDesde(summary.fim_ultimo_projeto);

  if (efetiva === "indisponivel") return "Disse recentemente que não topa.";
  if (efetiva === "disponivel_em_breve") return "Projeto com a gente termina nos próximos dias.";
  if (summary.interesse === "topa_freela" && (frescor === "fresco" || frescor === "recente")) {
    return "Disse que topa há menos de um mês.";
  }
  if (carga === 0 && diasFimProjeto !== null && diasFimProjeto <= 30) {
    return "Terminou projeto com a gente há menos de um mês.";
  }
  if (summary.interesse === "topa_freela" && frescor === "envelhecendo") {
    return "Disse que topa há alguns meses.";
  }
  if (efetiva === "possivelmente_disponivel") {
    return summary.reconsulta_vencida
      ? "A data que ele deu para reperguntar já passou."
      : "Não topava na época, mas a resposta tem mais de três meses.";
  }
  if (efetiva === "vale_abordar_empregado") return "Empregado, mas é um caso de checar interesse em freela.";
  if (efetiva === "vale_abordar_freela") return "Atua como freelancer, mas nunca registramos o interesse.";
  if (efetiva === "vale_abordar_headroom") return "Alocado com a gente, mas com espaço na agenda.";
  if (efetiva === "abre_espaco_em_breve") return "Abre uma vaga na agenda nos próximos dias.";
  if (carga === 0 && diasFimProjeto !== null && diasFimProjeto <= 180) {
    return "Terminou projeto com a gente há menos de seis meses.";
  }
  if (carga > 0 && summary.next_freeing_date) return "Alocado, com liberação prevista em até um mês.";
  if (summary.interesse === "topa_freela") return "Disse que topa, mas há mais de três meses sem reconfirmar.";
  if (diasFimProjeto !== null) return "Último projeto com a gente foi há mais de seis meses.";
  if (carga > 0) return "Alocado, sem liberação próxima.";
  return "Informação antiga, vale reconfirmar.";
}

// ---------------------------------------------------------------------------
// Eixos como filtro (COR-355): itens de cada eixo, filtráveis individualmente.
// Chaves de filtro batem com a RPC (M171): disp_emprego / disp_interesse / disp_relacao.
// ---------------------------------------------------------------------------
export interface EixoFiltro {
  key: "disp_emprego" | "disp_interesse" | "disp_relacao";
  dimension: "situacao_emprego" | "interesse_freela" | "relacao_conosco";
  label: string;
  values: string[]; // ordem: mais quente primeiro; 'desconhecido' sempre por último
}
export const EIXOS_FILTRO: EixoFiltro[] = [
  {
    key: "disp_interesse",
    dimension: "interesse_freela",
    label: EIXO_LABEL.interesse_freela,
    values: ["topa_freela", "aberto_mas_ocupado", "nao_topa", "desconhecido"],
  },
  {
    key: "disp_emprego",
    dimension: "situacao_emprego",
    label: EIXO_LABEL.situacao_emprego,
    values: ["freela_ativo", "entre_trabalhos", "empregado_fixo", "desconhecido"],
  },
  {
    key: "disp_relacao",
    dimension: "relacao_conosco",
    label: EIXO_LABEL.relacao_conosco,
    values: ["trabalhando_conosco", "disponivel_em_breve", "ja_trabalhou_conosco", "nunca_trabalhou", "desconhecido"],
  },
];

const DESCRICAO_EIXO_VALOR: Record<string, string> = {
  empregado_fixo: "Tem emprego fixo hoje (em geral pelo LinkedIn).",
  freela_ativo: "Atua como freelancer hoje (em geral pelo LinkedIn).",
  entre_trabalhos: "Sem trabalho fixo no momento.",
  topa_freela: "Respondeu que topa fazer freela.",
  aberto_mas_ocupado: "Topa, mas está sem espaço no momento.",
  nao_topa: "Respondeu que não topa freela agora.",
  trabalhando_conosco: "Tem projeto com a gente agora.",
  disponivel_em_breve: "Avisou que fica livre em breve.",
  ja_trabalhou_conosco: "Já teve projeto com a gente.",
  nunca_trabalhou: "Nunca teve projeto com a gente.",
  desconhecido: "Sem informação registrada neste eixo.",
};
export function axisValueDescricao(value: string): string {
  return DESCRICAO_EIXO_VALOR[value] ?? "";
}

// ---------------------------------------------------------------------------
// Ecos agregado <-> eixos (indicativos, NUNCA filtram): a correspondência é
// aproximada porque a disponibilidade também depende de carga/ocupação.
// Cada entrada = pares (eixo -> valor) que caracterizam aquele status.
// ---------------------------------------------------------------------------
type ComposicaoPar = Partial<Record<"disp_emprego" | "disp_interesse" | "disp_relacao", string>>;
const COMPOSICAO_EFETIVA: Partial<Record<DisponibilidadeEfetiva, ComposicaoPar[]>> = {
  vale_abordar_headroom: [{ disp_relacao: "trabalhando_conosco" }],
  vale_abordar_empregado: [{ disp_emprego: "empregado_fixo", disp_interesse: "topa_freela" }, { disp_emprego: "empregado_fixo", disp_interesse: "desconhecido" }],
  vale_abordar_freela: [{ disp_emprego: "freela_ativo", disp_interesse: "desconhecido" }],
  disponivel: [{ disp_interesse: "topa_freela" }],
  possivelmente_disponivel: [{ disp_interesse: "nao_topa" }, { disp_interesse: "aberto_mas_ocupado" }],
  indisponivel: [{ disp_interesse: "nao_topa" }],
  disponivel_em_breve: [{ disp_relacao: "trabalhando_conosco" }, { disp_relacao: "disponivel_em_breve" }],
  abre_espaco_em_breve: [{ disp_relacao: "trabalhando_conosco" }],
  alocado_conosco: [{ disp_relacao: "trabalhando_conosco" }],
  vinculo_sem_projeto: [{ disp_relacao: "ja_trabalhou_conosco" }],
};

export type SelecaoEixos = Partial<Record<"disp_emprego" | "disp_interesse" | "disp_relacao", string[]>>;

/** Eco agregado -> eixos: valores de eixo que compõem os status selecionados. */
export function eixosCompostosPorSelecao(efetivas: string[]): SelecaoEixos {
  const eco: SelecaoEixos = {};
  for (const efetiva of efetivas) {
    for (const par of COMPOSICAO_EFETIVA[efetiva as DisponibilidadeEfetiva] ?? []) {
      for (const [eixoKey, valor] of Object.entries(par)) {
        const chave = eixoKey as keyof SelecaoEixos;
        if (!eco[chave]) eco[chave] = [];
        if (!eco[chave]!.includes(valor)) eco[chave]!.push(valor);
      }
    }
  }
  return eco;
}

/** Eco eixos -> agregado: status cuja composição está coberta pela seleção de eixos. */
export function efetivasEquivalentesASelecao(selecao: SelecaoEixos): DisponibilidadeEfetiva[] {
  const temSelecao = Object.values(selecao).some((valores) => (valores ?? []).length > 0);
  if (!temSelecao) return [];
  const resultado: DisponibilidadeEfetiva[] = [];
  for (const [efetiva, combinacoes] of Object.entries(COMPOSICAO_EFETIVA)) {
    const bate = (combinacoes ?? []).some((par) =>
      Object.entries(par).every(([eixoKey, valor]) => (selecao[eixoKey as keyof SelecaoEixos] ?? []).includes(valor)),
    );
    if (bate) resultado.push(efetiva as DisponibilidadeEfetiva);
  }
  return resultado;
}
